import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tScenarioOrigin, tScenarioStatus, SCENARIO_STATUS,
  tEventType, tSimState, INHERENT_LEVEL, tInherentLevel, tLikelihood, tImpact, tRiskConfidence,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { EffBadge } from "@/components/shared/ControlsPanel";
import { CritBadge, SevBadge } from "@/components/shared/Badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  GitBranch, ArrowLeft, Loader2, CheckCircle2, Archive, Crosshair, ShieldAlert, Server,
  Target, Gauge, ShieldCheck, Info, ArrowRight, Activity, Play, Clock,
} from "lucide-react";

function Section({ title, icon: Icon, testid, children, note }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="font-head font-semibold text-sm">{title}</h2>
      </div>
      <div className="p-6 space-y-3">
        {note && <p className="text-[11px] text-muted-foreground leading-snug">{note}</p>}
        {children}
      </div>
    </section>
  );
}

function StatusBadge({ value }) {
  const c = SCENARIO_STATUS[value] || SCENARIO_STATUS.draft;
  return <Badge variant="outline" className={`rounded-sm text-xs font-mono ${c.cls}`} data-testid={`scenario-status-${value}`}>{tScenarioStatus(value)}</Badge>;
}

function LevelBadge({ level }) {
  const c = INHERENT_LEVEL[level] || INHERENT_LEVEL.LOW;
  return <Badge variant="outline" className={`rounded-sm font-mono text-xs ${c.cls}`}>{tInherentLevel(level)}</Badge>;
}

export default function ScenarioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const qc = useQueryClient();

  const { data: s, isLoading } = useQuery({
    queryKey: ["scenario", id], queryFn: async () => (await api.get(`/scenarios/${id}`)).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["scenario", id] });
  const validateM = useMutation({
    mutationFn: async () => api.put(`/scenarios/${id}`, { status: "validated" }),
    onSuccess: () => { toast.success("Scénario validé (hypothèse structurée)"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const archiveM = useMutation({
    mutationFn: async () => api.delete(`/scenarios/${id}`),
    onSuccess: () => { toast.success("Scénario archivé"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [depth, setDepth] = useState(3);
  const runsQ = useQuery({
    queryKey: ["scenario-runs", id], queryFn: async () => (await api.get(`/scenarios/${id}/simulations`)).data,
  });
  const simulateM = useMutation({
    mutationFn: async () => api.post(`/scenarios/${id}/simulate`, { max_depth: depth }),
    onSuccess: (res) => {
      toast.success("Simulation exécutée");
      qc.invalidateQueries({ queryKey: ["scenario-runs", id] });
      navigate(`/simulations/${res.data.id}`);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <AppShell title="Scénario"><div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></AppShell>;
  if (!s) return <AppShell title="Scénario"><p className="text-sm text-muted-foreground">Scénario introuvable.</p><Button variant="outline" className="mt-4 rounded-sm gap-2" onClick={() => navigate("/scenarios")}><ArrowLeft className="h-4 w-4" /> Retour</Button></AppShell>;

  const canWrite = can("scenario:write");
  const canExecute = can("scenario:execute");

  return (
    <AppShell title={s.title} subtitle="Fiche scénario — hypothèse cyber STRUCTURÉE (Lot 5). Déclaratif : aucune simulation ni risque résiduel."
      actions={
        <div className="flex items-center gap-2">
          <StatusBadge value={s.status} />
          {canWrite && s.status === "draft" && <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => validateM.mutate()} data-testid="validate-scenario-button"><CheckCircle2 className="h-4 w-4" /> Valider l'hypothèse</Button>}
          {canWrite && s.status !== "archived" && <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => archiveM.mutate()} data-testid="archive-scenario-button"><Archive className="h-4 w-4" /> Archiver</Button>}
        </div>
      }>
      <div className="space-y-6" data-testid="scenario-detail">
        <Section title="Identité" icon={GitBranch} testid="section-scenario-identity">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Origine</div><div className="mt-1">{tScenarioOrigin(s.origin)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Menace</div><div className="mt-1">{s.threat ? <button className="text-primary hover:underline" onClick={() => navigate(`/threats/${s.threat.id}`)}>{s.threat.name}</button> : "—"}</div></div>
          </div>
          {s.description && <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-3">{s.description}</p>}
        </Section>

        <Section title={`Chaîne causale — ${(s.chain || []).length} étape(s)`} icon={Target} testid="section-scenario-chain"
          note="Séquence DÉCLARATIVE ordonnée (hypothèses / événements attendus). Aucune propagation calculée, aucun état réel.">
          {(s.chain || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucune étape.</p> : (
            <ol className="space-y-2">
              {s.chain.map((st, i) => (
                <li key={i} className="flex items-start gap-3 border border-border rounded-sm px-3 py-2" data-testid={`scenario-chain-step-${i}`}>
                  <span className="font-mono text-xs text-primary mt-0.5">{st.order}</span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{tEventType(st.event_type)}</Badge>
                      <span className="text-muted-foreground text-xs">→ {st.target_ref?.type} </span>
                      {st.technique_external_id && <Badge variant="outline" className="rounded-sm text-[10px] font-mono text-primary">{st.technique_external_id}</Badge>}
                      {st.expected_effect && <span className="text-xs text-muted-foreground">effet attendu : <span className="text-foreground">{tSimState(st.expected_effect)}</span></span>}
                    </div>
                    {st.note && <p className="text-[11px] text-muted-foreground mt-1">{st.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Section>

        <Section title="Références" icon={Crosshair} testid="section-scenario-refs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Techniques ATT&CK</div>
              <div className="flex flex-wrap gap-1.5">{(s.techniques || []).map((t) => <Badge key={t.external_id} variant="outline" className="rounded-sm text-[10px] font-mono">{t.external_id} — {t.name}</Badge>)}{(s.techniques || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Vulnérabilités</div>
              <div className="space-y-1">{(s.vulnerabilities || []).map((v) => <button key={v.id} className="flex items-center gap-2 text-xs hover:text-primary" onClick={() => navigate(`/vulnerabilities/${v.id}`)}><ShieldAlert className="h-3.5 w-3.5" /> {v.title} <SevBadge value={v.severity} /></button>)}{(s.vulnerabilities || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Points d'entrée (actifs)</div>
              <div className="space-y-1">{(s.initial_nodes || []).map((n) => <span key={n.id} className="flex items-center gap-2 text-xs"><Server className="h-3.5 w-3.5 text-muted-foreground" /> {n.name} <CritBadge value={n.criticality} /></span>)}{(s.initial_nodes || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Services cibles</div>
              <div className="space-y-1">{(s.target_services || []).map((n) => <button key={n.id} className="flex items-center gap-2 text-xs hover:text-primary" onClick={() => navigate(`/services/${n.id}`)}><Target className="h-3.5 w-3.5" /> {n.name} <CritBadge value={n.criticality} /></button>)}{(s.target_services || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}</div>
            </div>
          </div>
        </Section>

        <Section title="Risque inhérent des services cibles (contexte)" icon={Gauge} testid="section-scenario-risk-context" note={s.risk_context?.note}>
          {(s.risk_context?.services || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucun service cible.</p> : (
            <div className="space-y-2">
              {s.risk_context.services.map((r) => (
                <div key={r.service.id} className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-sm px-3 py-2" data-testid={`scenario-risk-${r.service.id}`}>
                  <span className="text-sm font-medium">{r.service.name}</span>
                  <span className="flex items-center gap-3 text-xs font-mono text-muted-foreground">
                    <LevelBadge level={r.level} />
                    <span>{tLikelihood(r.likelihood)} × {tImpact(r.impact)}</span>
                    <span>Confiance {tRiskConfidence(r.confidence)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Contrôles pertinents (contexte)" icon={ShieldCheck} testid="section-scenario-controls-context" note={s.controls_context?.note}>
          {(s.controls_context?.controls || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucun contrôle associé aux services cibles / vulnérabilités.</p> : (
            <div className="space-y-2">
              <p className="text-[11px] font-mono text-muted-foreground">{s.controls_context.creditable_count} contrôle(s) à efficacité créditable — <span className="text-yellow-400">non appliqué(s)</span>.</p>
              {s.controls_context.controls.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2" data-testid={`scenario-control-${c.id}`}>
                  <span className="text-sm font-medium">{c.name}</span>
                  <EffBadge eff={c.effectiveness} />
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Simulation what-if (Lot 7)" icon={Activity} testid="section-scenario-simulation"
          note="Exécute le scénario sur le graphe (BFS déterministe, oracle de résilience injecté, effet des contrôles fonction-dépendant). Résultat isolé et immuable — aucune modification de l'état réel, aucun risque résiduel.">
          <div className="flex flex-wrap items-center gap-3">
            {canExecute ? (
              <>
                <label className="text-xs text-muted-foreground font-mono">Profondeur max (1–10)</label>
                <input type="number" min={1} max={10} value={depth} onChange={(e) => setDepth(Number(e.target.value))}
                  className="w-20 bg-background border border-border rounded-sm px-2 py-1 text-sm font-mono" data-testid="sim-depth-input" />
                <Button size="sm" className="rounded-sm gap-2" disabled={simulateM.isPending} onClick={() => simulateM.mutate()} data-testid="run-simulation-button">
                  {simulateM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Lancer la simulation
                </Button>
              </>
            ) : <p className="text-xs text-muted-foreground">Permission <code className="font-mono">scenario:execute</code> requise pour lancer une simulation.</p>}
          </div>
          <div className="mt-4 space-y-2" data-testid="simulation-runs-list">
            {(runsQ.data || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucune simulation exécutée.</p> : (
              (runsQ.data || []).map((r) => (
                <button key={r.id} onClick={() => navigate(`/simulations/${r.id}`)} data-testid={`sim-run-${r.id}`}
                  className="w-full flex flex-wrap items-center justify-between gap-3 border border-border rounded-sm px-3 py-2 hover:border-primary text-left transition-colors">
                  <span className="text-xs font-mono text-muted-foreground flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{new Date(r.created_at).toLocaleString("fr-FR")}</span>
                  <span className="flex items-center gap-3 text-xs font-mono">
                    <span>profondeur {r.params?.max_depth}</span>
                    <span>{r.impacted_services} service(s) impacté(s)</span>
                    {r.stats?.truncated && <Badge variant="outline" className="rounded-sm text-[10px] text-orange-400 border-orange-500/30">tronqué</Badge>}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              ))
            )}
          </div>
        </Section>
      </div>
    </AppShell>
  );
}
